import { FC } from 'react';
import { Mail } from 'lucide-react';
import * as Yup from 'yup';
import { useTranslation } from '@hooks/useTranslation';

const emailSchema = Yup.string().email();

export type ClientInviteNoticeVariant = 'invite' | 'replace';

/**
 * Sprint 47, story-235 (D3). Decides whether the notice shows and which wording. Show when the
 * trimmed email is a parseable address AND NOT (a login is linked AND the email equals the saved
 * one). `replace` wording when a login is linked and the email differs. Hidden for an empty field
 * and for a removed email on a linked client (the existing remove confirmation covers that).
 */
export const getClientInviteNoticeVariant = (
  email: string | null | undefined,
  savedEmail: string | null | undefined,
  hasLinkedUser: boolean,
): ClientInviteNoticeVariant | null => {
  const typed = (email ?? '').trim();
  if (!typed || !emailSchema.isValidSync(typed)) return null;
  if (hasLinkedUser) {
    return typed === (savedEmail ?? '').trim() ? null : 'replace';
  }
  return 'invite';
};

interface ClientInviteNoticeProps {
  email: string;
  savedEmail?: string | null;
  hasLinkedUser: boolean;
}

/**
 * Sprint 47, story-235. Saving a Client with an email still provisions a CLIENT login
 * (story-128, decision D3); this makes that visible before save. Not dismissible, never
 * disables Save. The typed address is plain text, never a link.
 */
export const ClientInviteNotice: FC<ClientInviteNoticeProps> = ({
  email,
  savedEmail,
  hasLinkedUser,
}) => {
  const tClients = useTranslation('clients');
  const variant = getClientInviteNoticeVariant(email, savedEmail, hasLinkedUser);
  if (!variant) return null;

  const template =
    variant === 'replace'
      ? tClients.emailInviteNoticeReplace
      : tClients.emailInviteNotice;

  return (
    <div
      role="note"
      data-testid="client-invite-notice"
      className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-100">
      <Mail aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="break-words [overflow-wrap:anywhere]">
        {template.replace('{email}', email.trim())}
      </p>
    </div>
  );
};

export default ClientInviteNotice;
